import React, { useState } from "react";
import Alert, { AlertColor } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Box from "@mui/material/Box";
import { ApiBannerMessage } from "../contexts/HttpClientContext";

const BannerMessage = ({ bannerMessage }: { bannerMessage: ApiBannerMessage }) => {
    const bannerId = bannerMessage.id !== undefined ? bannerMessage.id.toString() : bannerMessage.title;
    const storageKey = `minimized_banners`;

    const [minimized, setMinimized] = useState<boolean>(() => {
        const storedValue = localStorage.getItem(storageKey);
        if (storedValue) {
            try {
                const parsed = JSON.parse(storedValue);
                return !!parsed[bannerId];
            } catch (e) {
                return false;
            }
        }
        return false;
    });

    const toggleMinimized = () => {
        setMinimized((prev) => {
            const newValue = !prev;

            const storedValue = localStorage.getItem(storageKey);
            let parsed: Record<string, boolean> = {};
            if (storedValue) {
                try {
                    parsed = JSON.parse(storedValue);
                } catch (e) {}
            }

            parsed[bannerId] = newValue;
            localStorage.setItem(storageKey, JSON.stringify(parsed));

            return newValue;
        });
    };

    var title = bannerMessage.title;
    var message = bannerMessage.message;
    const tagRegex = /<\s*(script|iframe|object|embed|link|style|meta|form|input|button|textarea|select|option|applet|base|basefont|bgsound|blink|body|frame|frameset|head|html|ilayer|layer|link|marquee|noembed|noframes|noscript|plaintext|xml|xmp)[^>]*>(.*?)<\s*\/\s*\1\s*>/gi;
    title = title.replace(tagRegex, '');
    message = message.replace(tagRegex, '');

    // Remove dangerous attributes like on* (e.g., onclick, onload)
    const attrRegex = /\s*on\w+="[^"]*"/gi;
    title = title.replace(attrRegex, '');
    message = message.replace(attrRegex, '');

    // Remove javascript: and data: URIs
    const uriRegex = /\s*(href|src)="(javascript|data):[^"]*"/gi;
    title = title.replace(uriRegex, '');
    message = message.replace(uriRegex, '');

    const alerts = [
        "success",
        "info",
        "warning",
        "error"
    ];

    const actionButton = (
        <IconButton
            aria-label="expand banner"
            size="small"
            onClick={toggleMinimized}
            sx={{ color: "inherit" }}
        >
            {minimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
    );

    if (alerts.indexOf(bannerMessage.type) > -1) {
        return (
            <Alert
                icon={false}
                severity={bannerMessage.type as AlertColor}
                sx={{ color: "#000000" }}
                action={actionButton}
            >
                {title && (
                    <AlertTitle variant="h4" dangerouslySetInnerHTML={{ __html: title }} sx={{ mb: minimized ? 0 : 1, mt: minimized ? 0.5 : 0 }} />
                )}
                <Collapse in={!minimized}>
                    {message && (
                        <Typography variant="body1" dangerouslySetInnerHTML={{ __html: message }} />
                    )}
                </Collapse>
            </Alert>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                bgcolor: "rgb(255, 255, 255)",
                padding: 2,
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    {title && (
                        <Typography
                            variant="h4"
                            mb={minimized ? 0 : 2}
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                    )}
                </Box>
                {actionButton}
            </Box>
            <Collapse in={!minimized}>
                {message && (
                    <Typography variant="body1" dangerouslySetInnerHTML={{ __html: message }} />
                )}
            </Collapse>
        </Paper>
    );
}

export default BannerMessage;